import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faSave,
  faSpinner,
  faInfoCircle,
  faToggleOn,
  faToggleOff
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const PromptEditor = ({ prompt, agentId, isOpen, onClose, onSave, mode = 'edit' }) => {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    prompt_template: '',
    model: 'gpt-4o',
    temperature: 0.7,
    top_p: 1.0,
    max_output_tokens: null,
    output_format: 'text',
    output_schema: '',
    tool_enabled: false,
    tool_choice: 'none',
    text_format_name: '',
    text_format_type: 'text',
    text_format_schema: '',
    text_format_strict: false,
    stream: false
  });

  // Initialize form data when prompt changes
  useEffect(() => {
    if (prompt && mode === 'edit') {
      setFormData({
        name: prompt.name || '',
        description: prompt.description || '',
        prompt_template: prompt.prompt_template || '',
        model: prompt.model || 'gpt-4o',
        temperature: prompt.temperature || 0.7,
        top_p: prompt.top_p || 1.0,
        max_output_tokens: prompt.max_output_tokens || '',
        output_format: prompt.output_format || 'text',
        output_schema: prompt.output_schema ? 
          (typeof prompt.output_schema === 'string' ? prompt.output_schema : JSON.stringify(prompt.output_schema, null, 2)) : '',
        tool_enabled: prompt.tool_enabled || false,
        tool_choice: prompt.tool_choice || 'none',
        text_format_name: prompt.text_format_name || '',
        text_format_type: prompt.text_format_type || 'text',
        text_format_schema: prompt.text_format_schema ? 
          (typeof prompt.text_format_schema === 'string' ? prompt.text_format_schema : JSON.stringify(prompt.text_format_schema, null, 2)) : '',
        text_format_strict: prompt.text_format_strict || false,
        stream: prompt.stream || false
      });
    } else if (mode === 'create') {
      // Reset form for create mode
      setFormData({
        name: '',
        description: '',
        prompt_template: '',
        model: 'gpt-4o',
        temperature: 0.7,
        top_p: 1.0,
        max_output_tokens: '',
        output_format: 'text',
        output_schema: '',
        tool_enabled: false,
        tool_choice: 'none',
        text_format_name: '',
        text_format_type: 'text',
        text_format_schema: '',
        text_format_strict: false,
        stream: false
      });
    }
  }, [prompt, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const submitData = { ...formData };
      
      // Parse JSON fields if they're strings
      if (submitData.output_schema) {
        try {
          submitData.output_schema = JSON.parse(submitData.output_schema);
        } catch (e) {
          toast.error('Invalid JSON in Output Schema');
          setIsLoading(false);
          return;
        }
      }

      if (submitData.text_format_schema) {
        try {
          submitData.text_format_schema = JSON.parse(submitData.text_format_schema);
        } catch (e) {
          toast.error('Invalid JSON in Text Format Schema');
          setIsLoading(false);
          return;
        }
      }

      // Convert empty strings to null for numeric fields
      if (submitData.max_output_tokens === '') {
        submitData.max_output_tokens = null;
      }

      let response;
      if (mode === 'create') {
        response = await fetch(`/api/agents/${agentId}/settings/prompts`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(submitData)
        });
      } else {
        response = await fetch(`/api/agents/${agentId}/settings/prompts/${prompt.prompt_id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(submitData)
        });
      }

      if (response.ok) {
        toast.success(`Prompt ${mode === 'create' ? 'created' : 'updated'} successfully!`);
        onSave && onSave();
        onClose();
      } else {
        throw new Error(`Failed to ${mode} prompt`);
      }
    } catch (err) {
      toast.error(err.message);
      console.error('Error saving prompt:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content prompt-editor-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {mode === 'create' ? 'Create New Prompt' : `Edit Prompt: ${prompt?.name}`}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="prompt-form">
          <div className="form-sections">
            {/* Basic Information Section */}
            <div className="form-section">
              <h3>📝 Basic Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    placeholder="Enter prompt name"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter prompt description"
                    rows="3"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Prompt Template *</label>
                  <textarea
                    value={formData.prompt_template}
                    onChange={(e) => handleInputChange('prompt_template', e.target.value)}
                    required
                    placeholder="Enter your prompt template here..."
                    rows="8"
                    className="code-textarea"
                  />
                </div>
              </div>
            </div>

            {/* Model Configuration Section */}
            <div className="form-section">
              <h3>🤖 Model Configuration</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Model</label>
                  <select
                    value={formData.model}
                    onChange={(e) => handleInputChange('model', e.target.value)}
                  >
                    <option value="gpt-4o">GPT-4o</option>
                    <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Temperature</label>
                  <input
                    type="number"
                    min="0"
                    max="2"
                    step="0.1"
                    value={formData.temperature}
                    onChange={(e) => handleInputChange('temperature', parseFloat(e.target.value))}
                  />
                  <small>Controls randomness (0 = deterministic, 2 = very creative)</small>
                </div>

                <div className="form-group">
                  <label>Top P</label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={formData.top_p}
                    onChange={(e) => handleInputChange('top_p', parseFloat(e.target.value))}
                  />
                  <small>Controls nucleus sampling</small>
                </div>

                <div className="form-group">
                  <label>Max Output Tokens</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_output_tokens}
                    onChange={(e) => handleInputChange('max_output_tokens', e.target.value)}
                    placeholder="Optional"
                  />
                  <small>Maximum number of tokens to generate</small>
                </div>
              </div>
            </div>

            {/* Output Configuration Section */}
            <div className="form-section">
              <h3>📤 Output Configuration</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Output Format</label>
                  <select
                    value={formData.output_format}
                    onChange={(e) => handleInputChange('output_format', e.target.value)}
                  >
                    <option value="text">Text</option>
                    <option value="json">JSON</option>
                    <option value="json_schema">JSON Schema</option>
                  </select>
                </div>

                {formData.output_format !== 'text' && (
                  <div className="form-group full-width">
                    <label>Output Schema (JSON)</label>
                    <textarea
                      value={formData.output_schema}
                      onChange={(e) => handleInputChange('output_schema', e.target.value)}
                      placeholder='{"type": "object", "properties": {...}}'
                      rows="4"
                      className="code-textarea"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Text Format Name</label>
                  <input
                    type="text"
                    value={formData.text_format_name}
                    onChange={(e) => handleInputChange('text_format_name', e.target.value)}
                    placeholder="Optional format name"
                  />
                </div>

                <div className="form-group">
                  <label>Text Format Type</label>
                  <select
                    value={formData.text_format_type}
                    onChange={(e) => handleInputChange('text_format_type', e.target.value)}
                  >
                    <option value="text">Text</option>
                    <option value="json_schema">JSON Schema</option>
                    <option value="json_object">JSON Object</option>
                  </select>
                </div>

                {formData.text_format_type !== 'text' && (
                  <div className="form-group full-width">
                    <label>Text Format Schema (JSON)</label>
                    <textarea
                      value={formData.text_format_schema}
                      onChange={(e) => handleInputChange('text_format_schema', e.target.value)}
                      placeholder='{"type": "object", "properties": {...}}'
                      rows="4"
                      className="code-textarea"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Tool Configuration Section */}
            <div className="form-section">
              <h3>🔧 Tool Configuration</h3>
              <div className="form-grid">
                <div className="form-group toggle-group full-width">
                  <div className="toggle-wrapper">
                    <button
                      type="button"
                      className={`toggle-btn ${formData.tool_enabled ? 'active' : ''}`}
                      onClick={() => handleInputChange('tool_enabled', !formData.tool_enabled)}
                    >
                      <FontAwesomeIcon icon={formData.tool_enabled ? faToggleOn : faToggleOff} />
                      <span>Enable Tools</span>
                    </button>
                    <div className="toggle-info">
                      <FontAwesomeIcon icon={faInfoCircle} />
                      <span>Allow the AI to use external tools and functions</span>
                    </div>
                  </div>
                </div>

                {formData.tool_enabled && (
                  <div className="form-group">
                    <label>Tool Choice</label>
                    <select
                      value={formData.tool_choice}
                      onChange={(e) => handleInputChange('tool_choice', e.target.value)}
                    >
                      <option value="none">None</option>
                      <option value="auto">Auto</option>
                      <option value="required">Required</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Advanced Options Section */}
            <div className="form-section">
              <h3>⚙️ Advanced Options</h3>
              <div className="form-grid">
                <div className="form-group toggle-group">
                  <button
                    type="button"
                    className={`toggle-btn ${formData.stream ? 'active' : ''}`}
                    onClick={() => handleInputChange('stream', !formData.stream)}
                  >
                    <FontAwesomeIcon icon={formData.stream ? faToggleOn : faToggleOff} />
                    <span>Enable Streaming</span>
                  </button>
                </div>

                <div className="form-group toggle-group">
                  <button
                    type="button"
                    className={`toggle-btn ${formData.text_format_strict ? 'active' : ''}`}
                    onClick={() => handleInputChange('text_format_strict', !formData.text_format_strict)}
                  >
                    <FontAwesomeIcon icon={formData.text_format_strict ? faToggleOn : faToggleOff} />
                    <span>Strict Text Format</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  {mode === 'create' ? 'Creating...' : 'Saving...'}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  {mode === 'create' ? 'Create Prompt' : 'Save Changes'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromptEditor;